import React, { useEffect, useState, useCallback } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ArrowLeft, ScrollText, Loader2, RefreshCw, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { parseServerDate } from './utils/datetime';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = parseServerDate(iso);
  if (!d || Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN');
};

function Section({ title, testId, data, columns, page, setPage, pageSize }) {
  const total = data?.total || 0;
  const items = data?.items || [];
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="bg-slate-900/70 border border-slate-800 rounded-xl overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-white uppercase tracking-wider">{title}</h2>
        <span className="text-xs text-slate-400">{total.toLocaleString('en-IN')} entr{total === 1 ? 'y' : 'ies'}</span>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-sm">No entries match.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-950/60">
            <tr className="text-[10px] uppercase tracking-wider text-slate-400">
              {columns.map((c) => (
                <th key={c.key} className="px-4 py-2 text-left">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800/70">
            {items.map((row, idx) => (
              <tr key={row.id || idx} className="hover:bg-slate-800/30" data-testid={`${testId}-row-${idx}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-4 py-2 text-slate-200 truncate max-w-xs">
                    {c.render ? c.render(row) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {total > pageSize && (
        <div className="border-t border-slate-800 px-4 py-2 flex items-center justify-between text-xs text-slate-400">
          <div>Page {page} of {totalPages}</div>
          <div className="flex gap-2">
            <Button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Prev</Button>
            <Button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages} variant="outline" size="sm" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminCentralLogsPage() {
  const { admin, loading } = useAdminAuth();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [search, setSearch] = useState('');
  const [licensePage, setLicensePage] = useState(1);
  const [emailPage, setEmailPage] = useState(1);
  const [webhookPage, setWebhookPage] = useState(1);
  const [userAuditPage, setUserAuditPage] = useState(1);
  const pageSize = 25;

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      const params = new URLSearchParams({
        license_page: String(licensePage),
        email_page: String(emailPage),
        webhook_page: String(webhookPage),
        user_audit_page: String(userAuditPage),
        page_size: String(pageSize),
      });
      if (search.trim()) params.set('search', search.trim());
      const r = await adminApi.get(`/admin/central-logs?${params.toString()}`);
      setData(r.data);
    } catch {
      toast.error('Could not load central logs');
    } finally {
      setFetching(false);
    }
  }, [licensePage, emailPage, webhookPage, userAuditPage, search]);

  useEffect(() => { if (admin) refresh(); }, [admin, refresh]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div
      data-testid="admin-central-logs-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" data-testid="central-logs-back" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <ScrollText className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-white font-semibold">Central Logs</div>
            <div className="text-xs text-slate-400">Aggregated audit trail across licensing, email delivery, and inbound webhooks.</div>
          </div>
          <Button onClick={refresh} disabled={fetching} variant="outline" size="sm" data-testid="central-logs-refresh" className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${fetching ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        <div className="bg-slate-900/70 border border-slate-800 rounded-xl p-4 flex items-center gap-3">
          <Search className="w-4 h-4 text-slate-500" />
          <Input
            data-testid="central-logs-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setLicensePage(1); setEmailPage(1); setWebhookPage(1); setUserAuditPage(1); refresh(); } }}
            placeholder="Search across order id / license code / email / event / impersonation purpose…"
            className="bg-slate-800/50 border-slate-700 text-white"
          />
          <Button onClick={() => { setLicensePage(1); setEmailPage(1); setWebhookPage(1); setUserAuditPage(1); refresh(); }} size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-950" data-testid="central-logs-search-btn">
            Search
          </Button>
        </div>

        {fetching && !data ? (
          <div className="flex items-center gap-2 text-slate-400 text-sm py-10 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <Section
              title="Licensing events"
              testId="central-logs-licenses"
              data={data?.license_logs}
              page={licensePage}
              setPage={setLicensePage}
              pageSize={pageSize}
              columns={[
                { key: 'created_at', label: 'When', render: (r) => <span className="font-mono text-xs text-slate-400">{fmtTime(r.created_at)}</span> },
                { key: 'event_type', label: 'Event', render: (r) => <span className="font-bold text-amber-300 uppercase text-[10px]">{r.event_type}</span> },
                { key: 'license_code', label: 'License' },
                { key: 'order_id', label: 'Order ID' },
                { key: 'actor_email', label: 'Actor' },
              ]}
            />
            <Section
              title="Email deliveries"
              testId="central-logs-emails"
              data={data?.email_logs}
              page={emailPage}
              setPage={setEmailPage}
              pageSize={pageSize}
              columns={[
                { key: 'created_at', label: 'When', render: (r) => <span className="font-mono text-xs text-slate-400">{fmtTime(r.created_at)}</span> },
                { key: 'status', label: 'Status', render: (r) => <span className={`uppercase text-[10px] font-bold ${r.status === 'sent' ? 'text-emerald-300' : 'text-red-300'}`}>{r.status}</span> },
                { key: 'provider', label: 'Provider' },
                { key: 'recipient', label: 'Recipient' },
                { key: 'subject', label: 'Subject' },
                { key: 'purpose', label: 'Purpose' },
              ]}
            />
            <Section
              title="Inbound webhook deliveries"
              testId="central-logs-webhooks"
              data={data?.webhook_deliveries}
              page={webhookPage}
              setPage={setWebhookPage}
              pageSize={pageSize}
              columns={[
                { key: 'received_at', label: 'When', render: (r) => <span className="font-mono text-xs text-slate-400">{fmtTime(r.received_at)}</span> },
                { key: 'status', label: 'Status', render: (r) => <span className="uppercase text-[10px] font-bold text-slate-300">{r.status}</span> },
                { key: 'event_id', label: 'Event ID' },
                { key: 'order_id', label: 'Order ID' },
                { key: 'reason', label: 'Reason' },
              ]}
            />
            <Section
              title={<span className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-amber-400" />User impersonation audit</span>}
              testId="central-logs-user-audit"
              data={data?.user_audit_logs}
              page={userAuditPage}
              setPage={setUserAuditPage}
              pageSize={pageSize}
              columns={[
                { key: 'created_at', label: 'When', render: (r) => <span className="font-mono text-xs text-slate-400">{fmtTime(r.created_at)}</span> },
                { key: 'admin_email', label: 'Admin', render: (r) => (
                  <div className="flex flex-col">
                    <span className="text-slate-200">{r.admin_email || '—'}</span>
                    <span className="text-[10px] uppercase tracking-wider text-amber-300/70">{r.admin_role || ''}</span>
                  </div>
                ) },
                { key: 'target_email', label: 'Target user', render: (r) => (
                  <div className="flex flex-col">
                    <span className="text-slate-200">{r.target_email || '—'}</span>
                    <span className="text-[10px] text-slate-500 truncate max-w-[200px]">{r.target_organization_name || ''}</span>
                  </div>
                ) },
                { key: 'purpose', label: 'Purpose', render: (r) => (
                  <span className="text-slate-300 italic" title={r.purpose}>{r.purpose || '—'}</span>
                ) },
                { key: 'ip_address', label: 'IP', render: (r) => <span className="font-mono text-xs text-slate-400">{r.ip_address || '—'}</span> },
              ]}
            />
          </>
        )}
      </main>
    </div>
  );
}
